import { BrowserModule, BrowserTransferStateModule, TransferState, ɵDomSharedStylesHost, ɵgetDOM } from '@angular/platform-browser';
import { APP_INITIALIZER, Inject, Injector, NgModule, Optional, PLATFORM_ID } from '@angular/core';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { HttpClient, HttpClientModule } from '@angular/common/http';
import { isPlatformBrowser, LOCATION_INITIALIZED } from '@angular/common';

import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { NgxPageScrollCoreModule } from 'ngx-page-scroll-core';
import { NgxPageScrollModule } from 'ngx-page-scroll';
import { ResponsiveModule } from 'ngx-responsive';

import { GlobalComponentService, GlobalModule, TmModalComponent } from '@techmore/global';
// import { LoadingModule } from '@techmore/loading';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { GeneratorEnv, GENERATOR_ENV_TOKEN } from './web-generator/generator-env-token';
import { environment } from 'src/environments/environment';
import { MemberProxyModule } from './member/member-proxy.module';
import { BackStagePayload } from './web-generator/template-generator/entity/back-stage-payload';
import { Subject } from 'rxjs';
import { ServerConfigService } from './share/service/server-config.service';
import { BackStageTemplateService } from './share/service/back-stage-template.service';
import { translateBrowserLoaderFactory } from './share/service/translate-browser-loader.service';
import { EventReplayer, PrebootModule } from 'preboot';
import { TmFroalaEditorModule } from '@techmore/froala-editor';
import { RESPONSE } from '@nguniversal/express-engine/tokens';
import { Response } from 'express';
import { CustomDomSharedStylesHost } from './custom-dom-shared-styles-host';
import { CSP_NONCE_KEY } from './share/tokens/csp-nonce.statekey';
import { CSP_NONCE_TOKEN } from './share/tokens/csp-nonce.token';
import { LoggerService } from './share/service/logger.service';
import { TokenService } from './share/service/token.service';
import { MemberProxyService } from './member/service/member-proxy.service';
import { resolve } from 'url';
import { Router } from '@angular/router';


export function initializationFactory(injector: Injector, serverConfigService: ServerConfigService, loggerService: LoggerService) {
  return () => new Promise<any>((resole: any) => {
    const locationInitialized = injector.get(LOCATION_INITIALIZED, Promise.resolve(null));
    const platformId = injector.get(PLATFORM_ID);
    const isBrowser = isPlatformBrowser(platformId);
    if (!isBrowser) {
      loggerService.info(`initializationFactory`);
    }

    if (isBrowser) {
      const dom = ɵgetDOM();
      const styles: any[] = Array.prototype.slice.apply(dom.querySelectorAll(document, `style[ng-transition]`));
      styles.forEach(el => {
        // Remove ng-transition attribute to prevent Angular appInitializerFactory
        // to remove server styles before preboot complete
        el.removeAttribute('ng-transition');
      });
      document.addEventListener('PrebootComplete', () => {
        // After preboot complete, remove the server scripts
        setTimeout(() => styles.forEach(el => dom.remove(el)));
      });
    }

    if (!serverConfigService) { serverConfigService = injector.get(ServerConfigService); }
    locationInitialized.then(() => {
      if (!isBrowser) {
        loggerService.info(`locationInitialized`);
      }
      let backStageSubj: Subject<BackStagePayload>;
      if (isBrowser) {
        const url = serverConfigService.url;
        if (serverConfigService.isEditModeUrl) { // 'aHR0cCUzQSUyRiUyRmxvY2FsaG9zdCUzQTQzMDA='
          // 如果是編輯模式的 url，直接進 CSR 畫面
          const replayer = injector.get(EventReplayer);
          replayer.replayAll();

          const queryParamSatrtIndex = url.indexOf('?');
          const url2 = url.slice(0, queryParamSatrtIndex);
          let stageUrl: string;
          let match: any;
          try {
            stageUrl = decodeURIComponent(atob(decodeURIComponent(serverConfigService.queryParams['stage'])));
            match = environment.allowBackStageDomain.find(domain => domain === stageUrl);
          } catch (e) {
            console.error(e);
          }
          const opener = window.opener;
          if (environment.production) {
            if (!opener || opener === window || !match || !stageUrl)  {
              if (!opener) {
                console.error('no opener');
              } else if (opener === window) {
                console.error('open self');
              } else if (!match) {
                console.error('allowBackStageDomain not match');
              }
              alert('請從後台進入!');
              window.location.href = url2;
            }
          }

          const urlSegs = url2.replace(/https?:\/\//g, '').split('/');
          const isIndexPage = urlSegs.length === 2;
          const isSubjectDetailPage = urlSegs.length === 4;
          window.opener.postMessage('appInit', stageUrl);
          const backStageTemplateService = injector.get(BackStageTemplateService);
          backStageSubj = backStageTemplateService.listenOnWindowMessage(isIndexPage, isSubjectDetailPage);
        }
      }

      serverConfigService.getConfig(backStageSubj).toPromise().then((config) => resole(config));
    });
  });
}

export function i18nLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

@NgModule({
  declarations: [
    AppComponent,
  ],
  imports: [
    BrowserModule.withServerTransition({ appId: 'serverApp' }),
    PrebootModule.withConfig({ appRoot: 'app-root', replay: false }),
    BrowserTransferStateModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    HttpClientModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: translateBrowserLoaderFactory,
        deps: [HttpClient, TransferState]
      },
      defaultLanguage: 'zh-tw',
    }),
    NgxPageScrollCoreModule,
    NgxPageScrollModule,
    ResponsiveModule.forRoot(),
    GlobalModule.forRoot(),
    MemberProxyModule.forRoot(),
    TmFroalaEditorModule.forRoot(),
  ],
  providers: [
    {provide: GENERATOR_ENV_TOKEN, useFactory: () => {
      return Object.assign(new GeneratorEnv(), {
        apiHost: environment.apiHost,
        allowBackStageDomain: environment.allowBackStageDomain,
        recaptchaKey: environment.recaptchaKey,
        commonToken: environment.commonToken,
        itriSearchApiHost: environment.itriSearchApiHost,
        itriAutoCompleteApiHost: environment.itriAutoCompleteApiHost,
        accessTokenKey: 'accessToken',
        refreshTokenKey: 'refreshToken',
        googleSiteVerification: environment.googleSiteVerification,
      });
    }, multi: false},
    {provide: APP_INITIALIZER, useFactory: initializationFactory, deps: [Injector, ServerConfigService, LoggerService], multi: true},
    { provide: CSP_NONCE_TOKEN, deps: [PLATFORM_ID, TransferState, [new Optional(), RESPONSE]], useFactory: (platformId: object, tState: TransferState, response: Response) => {
      if (isPlatformBrowser(platformId)) {
        return tState.get(CSP_NONCE_KEY, '');
      } else {
        return response.locals.cspNonce;
      }
    }},
    {provide: ɵDomSharedStylesHost, useClass: CustomDomSharedStylesHost},
  ],
  bootstrap: [AppComponent],
  entryComponents: [
    TmModalComponent
  ]
})
export class AppModule {
  constructor(
    protected injector: Injector,
    protected translate: TranslateService,
    @Inject(PLATFORM_ID) private platformId: object,
    memberCenterService: MemberProxyService,
    tokenService: TokenService,
    globalComponentService: GlobalComponentService,
    router: Router,
  ) {
    if (isPlatformBrowser(this.platformId)) {
      // localStorage.removeItem('site_token');

      tokenService.registCustomLogout(() => {
        return new Promise((resolve, reject) => {
          try {
            const jwt = tokenService.getJWTpayload();
            const expireDuration = Math.ceil((jwt.exp - jwt.iat) / 60);
            memberCenterService.logout(false);
            globalComponentService.openErrorAlert(`會員登入已閒置${expireDuration}分鐘，請您重新登入。`);
            // globalComponentService.openSanckBar('會員已被登出，請重新登入', 'X', {duration: 5000, horizontalPosition: 'right'});
            router.navigateByUrl('/zh-tw').then(_ => {
              resolve(_);
            }).catch(error => {
              reject(error);  
            });
          } catch (error) {
            reject(error);
          }
        });
      });
    }
    // this.translate.addLangs(['zh-tw']);
    // this.translate.setDefaultLang('zh-tw');
  }
}
